import React, { useEffect, useState, useCallback } from 'react'
import {
  type SelectProps,
  type SimpleOption,
} from '@retailer-platform/shared-components/src/common/select/utils/select.types'
import { Select } from '@retailer-platform/shared-components'
import { type FormContextValues } from 'react-hook-form'
import moment from 'moment'
import { intervalDifference } from 'domains/shifts/components/utils/intervalUtils'
import { useShiftsStateContext } from 'domains/shifts/components/contexts/ShiftsStateContext'
import { useShiftDetailsFormStyles } from 'domains/shifts/components/contexts/ShiftDetailsFormStylesContext'
import {
  type PartnerPickRecurringShiftsFormData,
  type TimeDropdownOptionType,
} from './recurringShift.types'
import { StyledLabel, TimeRangeDropDownContainer } from './ShiftDetailsForm.styles'

interface OptionType {
  label: string
  value: Number
}

interface SimpleOptionProps
  extends Omit<SelectProps<SimpleOption<OptionType>>, 'options' | 'isMulti'> {}

interface Props extends SimpleOptionProps {
  label: string
  name: string
  register: FormContextValues<PartnerPickRecurringShiftsFormData>['register']
  defaultValue: TimeDropdownOptionType
  setValue: FormContextValues<PartnerPickRecurringShiftsFormData>['setValue']
  getValues: FormContextValues<PartnerPickRecurringShiftsFormData>['getValues']
  options: TimeDropdownOptionType[]
  regularStoreOpen: Date
}

export const TimeRangeDropdown: React.FunctionComponent<Props> = ({
  label,
  name,
  register,
  defaultValue,
  setValue,
  getValues,
  options,
  regularStoreOpen,
  ...rest
}) => {
  const [selectedTimeOption, setSelectedTimeOption] = useState<OptionType>(defaultValue)
  const { updateSelectedRecommendationShiftRange } = useShiftsStateContext()

  const setFormValue = useCallback(
    option => {
      setValue(name, option, true)
    },
    [name, setValue]
  )

  const timeRangeNameMapping: { [key: string]: 'start' | 'end' } = {
    from: 'start',
    to: 'end',
  }

  const handleChange = option => {
    setSelectedTimeOption(option)
    setFormValue(option)
    const shiftIntervalEndpoint =
      intervalDifference(
        regularStoreOpen,
        moment()
          .startOf('day')
          .add(option.value * 1000)
          .toDate()
      ) + 1
    updateSelectedRecommendationShiftRange(timeRangeNameMapping[name], shiftIntervalEndpoint)
  }

  useEffect(() => {
    register({ name })
  }, [register, name])

  // Setup default value that comes from form definition
  useEffect(() => {
    const initial = getValues(name)
    setFormValue(initial)
  }, [getValues, name, setFormValue])

  const styles = useShiftDetailsFormStyles()

  return (
    <TimeRangeDropDownContainer data-testid="shopper-shift-form-time-range-from-container">
      <StyledLabel>{label}</StyledLabel>
      <Select
        name={name}
        defaultValue={selectedTimeOption}
        value={selectedTimeOption}
        onChange={option => handleChange(option)}
        options={options}
        styles={styles}
        {...rest}
      />
    </TimeRangeDropDownContainer>
  )
}
